import React from 'react'
import { connect, useDispatch } from 'react-redux'
import { useHistory, useRouteMatch } from 'react-router-dom'

import { setProjectIdentity } from '@store/actionSlices/projectIdentity'
import { setWhiteLabel } from '@store/actionSlices/whiteLabel'
import { RouteKey } from '@store/constants'
import { ProjectIdentity, RootStateTypeExtra } from '@store/types'

import {
  selectFromResult as selectFromConfigResult,
  useGetConfigQuery,
} from '@api/config'
import { ShowcaseRoomConfig } from '@api/showcase-room'
import {
  selectFromWhiteLabelResult,
  useGetWhiteLabelsQuery,
} from '@api/white-label'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import { SessionMap } from '@firebaseUtil/types'

import AssetHandler from '@adUtilities/asset-handler/asset-handler'
import useImageURLValidator from '@adUtilities/hooks/image-url-validator'

const DEFAULT_LOGO_URL = `${process.env.REACT_APP_BUCKET}/showcase_logo_dark_1280px.svg`

export interface BrandingProps {
  className?: string
  projectIdentity: ProjectIdentity
  showcaseRoomConfig: ShowcaseRoomConfig
  session: SessionMap | undefined
}

function Branding({
  className,
  projectIdentity,
  showcaseRoomConfig,
  session,
}: BrandingProps) {
  const dispatch = useDispatch()
  const history = useHistory()
  const isOnVisionPage = useRouteMatch('/vision')

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const {
    configData,
    isLoaded: isConfigLoaded,
    isError: configHasError,
  } = useGetConfigQuery(
    { projectName: projectIdentity.projectId },
    {
      selectFromResult: selectFromConfigResult,
      skip: !projectIdentity.projectId,
    }
  )

  const {
    whiteLabelData,
    isLoaded: isWhiteLabelLoaded,
    isError: whiteLabelHasError,
  } = useGetWhiteLabelsQuery(
    { projectName: projectIdentity.projectId },
    {
      selectFromResult: selectFromWhiteLabelResult,
      skip: !projectIdentity.projectId,
    }
  )

  const onLogoClick = async () => {
    const hasActiveModel = Boolean(session?.threeDView?.activeModel)
    if (hasActiveModel) {
      await firebaseControlQuery.update({
        'threeDView.activeModel': '',
        'threeDView.activeScene': '',
      })
    }

    history.push(RouteKey.VISION)
    await firebaseControlQuery.updateRoute(RouteKey.VISION)
  }

  React.useEffect(() => {
    if (isConfigLoaded && !configHasError) {
      dispatch(
        setProjectIdentity({
          ...projectIdentity,
          ...configData,
          ...showcaseRoomConfig,
        })
      )
    }
  }, [isConfigLoaded, showcaseRoomConfig])

  React.useEffect(() => {
    if (isWhiteLabelLoaded && !whiteLabelHasError) {
      dispatch(setWhiteLabel(whiteLabelData))
    }
  }, [isWhiteLabelLoaded])

  const defaultLogoUrl = React.useMemo(
    () =>
      AssetHandler({
        type: 'new',
        url: DEFAULT_LOGO_URL,
      }),
    []
  )

  const logoUrl = React.useMemo(() => {
    const {
      logo: { url: logoUrl, visible },
      theme: { remoteVisionLogo },
    } = projectIdentity
    let url = ''

    if (visible && logoUrl) {
      url = logoUrl
    }
    if (remoteVisionLogo?.url && isOnVisionPage) {
      url = remoteVisionLogo.url
    }

    if (url) {
      return AssetHandler({
        type: 'new',
        url,
      })
    }
    return undefined
  }, [projectIdentity, isOnVisionPage])

  const { validatedUrl: logoSrc, isLoading: isLogoLoading } =
    useImageURLValidator(logoUrl ?? defaultLogoUrl, defaultLogoUrl)

  return (
    <div className={className}>
      <button type="button" onClick={onLogoClick}>
        <img
          src={logoSrc}
          className={`max-h-[85px] w-full ${isLogoLoading ? 'hidden' : ''}`}
          alt="logo"
        />
      </button>
    </div>
  )
}

export default connect(
  ({
    projectIdentity,
    showcaseRoom: showcaseRoomConfig,
    firestore: { session },
  }: RootStateTypeExtra) => ({
    projectIdentity,
    showcaseRoomConfig,
    session,
  })
)(Branding)
